import { useEffect, useRef } from "react";

import { notify } from "@/lib/notify";
import { cn } from "@/lib/utils";

const BOX_PREVIEW_CDN = "https://cdn01.boxcdn.net/platform/preview/2.95.0/en-US";

interface BoxPreviewInstance {
  show: (
    fileId: string,
    accessToken: string,
    options: { container: HTMLElement; showDownload?: boolean },
  ) => void;
  hide: () => void;
}

declare global {
  interface Window {
    Box?: { Preview: new () => BoxPreviewInstance };
  }
}

let assetsPromise: Promise<void> | null = null;

/** Load the Box Content Preview CDN assets once (signature-page pattern: pages/sign/[id]/sign.vue). */
function loadBoxPreviewAssets(): Promise<void> {
  if (window.Box) {
    return Promise.resolve();
  }
  assetsPromise ??= new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = `${BOX_PREVIEW_CDN}/preview.js`;
    script.onload = () => resolve();
    script.onerror = () => {
      assetsPromise = null;
      reject(new Error("Box Preview assets failed to load"));
    };
    document.head.appendChild(script);

    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = `${BOX_PREVIEW_CDN}/preview.css`;
    document.head.appendChild(link);
  });
  return assetsPromise;
}

/** Renders a Box file (docx…) with Box UI Elements, from a downscoped preview token. */
export function BoxContentPreview({
  fileId,
  accessToken,
  className,
}: {
  fileId: string;
  accessToken: string;
  /** The preview dialog's 75vh by default; a full page passes its own height. */
  className?: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    let preview: BoxPreviewInstance | null = null;
    loadBoxPreviewAssets().then(
      () => {
        if (cancelled || !containerRef.current || !window.Box) {
          return;
        }
        preview = new window.Box.Preview();
        // The dialog has its own download button — hide Box's.
        preview.show(fileId, accessToken, { container: containerRef.current, showDownload: false });
      },
      () => notify.error("Impossible de charger l'aperçu Box"),
    );
    return () => {
      cancelled = true;
      preview?.hide();
    };
  }, [fileId, accessToken]);

  return <div ref={containerRef} className={cn("h-[75vh] w-full overflow-hidden rounded-md", className)} />;
}
